import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonTitle,
  IonToggle,
  IonToolbar,
  ScrollDetail,
  useIonAlert,
  useIonToast,
} from "@ionic/react";
import { useMachine } from "@xstate/react";
import {
  editMenuMachine,
  editCategoryMessage,
  editProductMessage,
  addProductMessage,
  removeProductMessage,
  byId,
  AddCategoryChange,
  MoveCategoryChange,
  MoveProductChange,
} from "./EditMenuMachine";
import { FloatingBackButton, LazyImage } from "@greeter/matter";
import {
  Category,
  Menu,
  MenuProduct,
  PriceChange,
  Product,
  ImageAsset,
} from "@greeter/core";
import { Query } from "@greeter/query";
import { mockMenu } from "@greeter/mock";

import { clamp } from "lodash";

import { classNames, doNothing } from "@greeter/util";
import {
  IconArrowBackUp,
  IconArrowBackUpDouble,
  IconArrowLeft,
  IconDeselect,
  IconDeviceFloppy,
  IconDragDrop2,
  IconEdit,
  IconHandGrab,
  IconInfoCircle,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { TimeInput } from "@greeter-venue/components/TimeInput";

import { Transport } from "@greeter/event";
import { IonContentCustomEvent, isPlatform } from "@ionic/core";
import { TimeOfDay } from "@greeter/date";
import { any, defined, empty } from "@greeter/array";

import { UploadButton } from "@greeter-venue/components/UploadInput";
import { logger } from "@greeter/log";
import { useLoading } from "@greeter/loading";

import { MaskitoOptions } from "@maskito/core";
import { useMaskito } from "@maskito/react";
import {
  maskitoCaretGuard,
  maskitoPostfixPostprocessorGenerator,
} from "@maskito/kit";

import css from "./EditMenuPage.module.scss";

const log = logger("[EditMenuPage]");

/**
 * TODO: Abstract this out into some utility library so we can make awesome styling and create cool scroll effects
 *
 * Creates the appropriate styling for step-based animation based on scrolling.
 * By using the ref we can avoid React rerenders.
 */
function zoomOutOnScrollRef(
  ref: HTMLDivElement,
  scrollTop: number,
  threshold: number = 50
) {
  const min = 0.9;
  const max = 1;
  const steps = (max - min) / threshold;
  const step = clamp(max - steps * scrollTop, min, max);
  const opacityStep = clamp(1 - (0.5 / threshold) * scrollTop, 0.5, 1);

  ref.style.transform = `scale(${step})`;
  ref.style.opacity = `${opacityStep}`;
}

type AddButtonProps = PropsWithChildren & {
  onClick?: () => void;
};

function AddButton({ onClick = doNothing, children }: AddButtonProps) {
  return (
    <button className={css.AddButton} onClick={onClick}>
      {children ? children : "+ Produkt"}
    </button>
  );
}

type AddButtonsProps = PropsWithChildren & {};

function AddButtons({ children }: AddButtonsProps) {
  return React.Children.count(children) === 0 ? (
    <></>
  ) : (
    <div {...classNames(css.AddContainer, css.FullBleed)}>
      <div className={css.DashedLine}></div>
      {React.Children.toArray(children)
        .filter(defined)
        .map((c, i) => {
          return (
            <React.Fragment key={`add-button-${i}`}>
              {c}
              {i !== React.Children.count(children) - 1 && (
                <div className={css.DashedLine} />
              )}
            </React.Fragment>
          );
        })}
      <div className={css.DashedLine}></div>
    </div>
  );
}

type AddPriceChangeButtonProps = {
  onClick?: () => void;
};

function AddPriceChangeButton({
  onClick = doNothing,
}: AddPriceChangeButtonProps) {
  return (
    <button onClick={onClick} className={css.DashedOutlineButton}>
      + Prisændring
    </button>
  );
}

type SimpleButtonProps = {
  onClick: () => void;
};

type UndoButtonProps = SimpleButtonProps;
function UndoButton(props: UndoButtonProps) {
  return (
    <button style={{ backgroundColor: "transparent" }} onClick={props.onClick}>
      <IconArrowBackUp style={{ color: "white" }} />
    </button>
  );
}

type UndoAllButtonProps = SimpleButtonProps;
function UndoAllButton(props: UndoAllButtonProps) {
  return (
    <button style={{ backgroundColor: "transparent" }} onClick={props.onClick}>
      <IconArrowBackUpDouble style={{ color: "white" }} />
    </button>
  );
}

type EditItemActionBarProps = PropsWithChildren & {
  slots?: {
    start?: React.ReactNode;
    end?: React.ReactNode;
  };
};
function EditItemActionBar(props: EditItemActionBarProps) {
  return (
    <div className={css.EditItemActionBar}>
      <div
        className={css.EditItemActionBarButtons}
        style={{ justifyContent: "start" }}
      >
        {props.slots?.start}
      </div>
      <div
        className={css.EditItemActionBarButtons}
        style={{ justifyContent: "center" }}
      >
        {props.children}
      </div>
      <div
        className={css.EditItemActionBarButtons}
        style={{ justifyContent: "end" }}
      >
        {props.slots?.end}
      </div>
    </div>
  );
}

type PriceChangeInputProps = {
  priceChange: PriceChange;
  onChange: (priceChange: PriceChange) => void;
  onDelete: (priceChange: PriceChange) => void;
};

function PriceChangeInput({
  priceChange,
  onChange = doNothing,
  onDelete = doNothing,
}: PriceChangeInputProps) {
  return (
    <div className={css.PriceChangeContainer}>
      <div {...classNames("labelled-input", css.From)}>
        <label>Fra</label>
        <TimeInput
          className={css.TimeInput}
          value={priceChange.period.from}
          onChange={(ev) => {
            onChange({
              ...priceChange,
              period: { ...priceChange.period, from: ev },
            });
          }}
        />
      </div>
      <div {...classNames("labelled-input", css.To)}>
        <label>Til</label>
        <TimeInput
          className={css.TimeInput}
          value={priceChange.period.to}
          onChange={(ev) => {
            onChange({
              ...priceChange,
              period: { ...priceChange.period, to: ev },
            });
          }}
        />
      </div>
      <div {...classNames(css["labelled-input"], css.Price)}>
        <label>Pris</label>
        <PriceInput
          {...classNames(css.Input)}
          onPriceChange={(price: number) => {
            onChange({ ...priceChange, price: price });
          }}
          value={priceChange.price}
        />
      </div>
      <button
        className={css.IconBtn}
        onClick={() => onDelete(priceChange)}
        style={{
          width: "100%",
          height: "100%",
          display: "grid",
          placeItems: "center",
          paddingTop: "16px",
        }}
      >
        <IconTrash className="fg-danger" />
      </button>
    </div>
  );
}

type ActionButtonProps = PropsWithChildren & {
  onClick: () => void;
  active?: boolean;
};
function ActionButton(props: ActionButtonProps) {
  return (
    <button
      {...props}
      {...classNames(css.IconBtn, css.ActionBtn, props.active && "fg-green")}
    />
  );
}

type FullBleedLayoutProps = PropsWithChildren & {};
function FullBleedLayout(props: FullBleedLayoutProps) {
  return <div className={css.FullBleedLayout} {...props} />;
}

type ActionLabelProps = PropsWithChildren & {};
function ActionLabel(props: ActionLabelProps) {
  return <span className={css.ActionBtnHint} {...props} />;
}

type MenuCapProps = {};
function MenuCap(props: MenuCapProps) {
  return <div className={css.MenuCap} />;
}

type EditItemProps = PropsWithChildren & {
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
};
function EditItem({ className, ...props }: EditItemProps) {
  return <div {...classNames(css.EditItem, className)} {...props} />;
}

type MenuItemProps = PropsWithChildren & {
  style?: React.CSSProperties;
  onClick?: () => void;
};
function MenuItem(props: MenuItemProps) {
  return <section className={css.MenuItem} {...props} />;
}

type MoveHereButtonProps = PropsWithChildren & { onClick?: () => void };
function MoveHereButton(props: MoveHereButtonProps) {
  return (
    <button {...props} className={css.MoveHereButton}>
      {props.children ? props.children : <span>&gt; Flyt hertil &lt;</span>}
    </button>
  );
}

type FloatingHintProps = PropsWithChildren & {};
function FloatingHint(props: FloatingHintProps) {
  return <div className={css.FloatingMoveHint} {...props} />;
}

type ActionBarProps = PropsWithChildren & {};
function ActionBar(props: ActionBarProps) {
  return (
    <div
      {...classNames(css.ActionBar, isPlatform("ios") && css.IsIos)}
      {...props}
    />
  );
}

type ChangeFrame = PropsWithChildren & {
  change?: "added" | "changed" | "nothing";
  className?: string;
  id?: string;
};
function ChangeFrame(props: ChangeFrame) {
  return (
    <div
      id={props.id}
      {...classNames(
        css.ChangeFrame,
        props.change === "added"
          ? css.Added
          : props.change === "changed" && css.Changed,
        props.className
      )}
    >
      {props.children}
    </div>
  );
}

type ThumbnailProps = {
  src: string;
  alt: string;
};
function Thumbnail(props: ThumbnailProps) {
  return (
    <LazyImage
      classNames={{
        container: css.ProductImage,
      }}
      style={{
        objectFit: "cover",
        objectPosition: "center",
      }}
      src={props.src}
      alt={props.alt}
    />
  );
}

type ToggleButtonProps = {
  checked?: boolean;
  onClick: () => void;
};
function ToggleButton(props: ToggleButtonProps) {
  return (
    <IonToggle
      mode="ios"
      enableOnOffLabels
      labelPlacement="stacked"
      checked={props.checked}
      onIonChange={props.onClick}
    />
  );
}

type ProductEditsActionBarProps = {
  changedProductIds: string[];
  productId: string;
  active?: boolean;
  onRemove: (productId: string) => void;
  onUndoAll: (productId: string) => void;
  onUndo: (productId: string) => void;
  onToggleActive: () => void;
};

function ProductEditsActionBar(props: ProductEditsActionBarProps) {
  return (
    <EditItemActionBar
      slots={{
        start: props.changedProductIds.find((_p) => _p === props.productId) && (
          <>
            <UndoAllButton onClick={() => props.onUndoAll(props.productId)} />
            <UndoButton onClick={() => props.onUndo(props.productId)} />
          </>
        ),
        end: (
          <button
            onClick={() => props.onRemove(props.productId)}
            className={css.IconBtn}
          >
            <IconTrash className="fg-danger" />
          </button>
        ),
      }}
    >
      <ToggleButton checked={props.active} onClick={props.onToggleActive} />
    </EditItemActionBar>
  );
}

type CategoryEditsActionBarProps = {
  changedCategories: string[];
  category: Category;
  onUndo: (category: Category) => void;
  onUndoAll: (category: Category) => void;
  onRemove: (category: Category) => void;
};

function CategoryEditsActionBar(props: CategoryEditsActionBarProps) {
  return (
    <EditItemActionBar
      slots={{
        start: props.changedCategories.find(findId(props.category.id)) && (
          <>
            <UndoAllButton onClick={() => props.onUndoAll(props.category)} />
            <UndoButton onClick={() => props.onUndo(props.category)} />
          </>
        ),
        end: (
          <button
            onClick={() => props.onRemove(props.category)}
            className={css.IconBtn}
          >
            <IconTrash className="fg-danger" />
          </button>
        ),
      }}
    />
  );
}

const postfix = " kr.";
const priceMask: MaskitoOptions = {
  mask: /^\$?\d*(,\d{0,2})?$/,
  postprocessors: [maskitoPostfixPostprocessorGenerator(postfix)],
  plugins: [maskitoCaretGuard((value) => [0, value.length - postfix.length])],
};

class PriceFormatter {
  /**
   * Expects a string the resembles '1234,23 kr.'
   */
  static fromDanish(input: string): number {
    // In Denmark the decimal place is specified by ',' instead of '.', we need to conver that
    const price = Number(input.split("kr.")[0].trim().replace(/,/g, "."));
    return price;
  }
}

type PriceInputProps = React.HTMLProps<HTMLInputElement> & {
  onPriceChange?: (price: number) => void;
};
function PriceInput({ className, value, ...props }: PriceInputProps) {
  const maskedInputRef = useMaskito({ options: priceMask });

  // used to maintain intermediary values such as , but without tainting the price
  const [_prevValue, setPrevValue] = useState("");
  const [_value, setValue] = useState("");

  /**
   * This ensures that orphan , are properly maintained when inserting data
   */
  useEffect(() => {
    if (!value) return;
    const vs = value.toString().replace(/\./, ",").split(postfix)[0];

    const deleting = _prevValue.length > `${value}`.length;

    setPrevValue(_value);

    if (_value === "") {
      setValue(`${vs} ${postfix}`);
      return;
    }

    if (vs.indexOf(",") === -1 && _value.indexOf(",") > -1 && !deleting) {
      setValue(`${vs}, ${postfix}`);
      return;
    }
  }, [value, _value]);

  return (
    <input
      {...classNames(css.Input, className)}
      ref={maskedInputRef}
      type="text"
      inputMode="decimal"
      onInput={(ev: React.InputEvent<HTMLInputElement>) => {
        if (props.onPriceChange)
          props.onPriceChange(
            PriceFormatter.fromDanish(ev.currentTarget.value)
          );

        setValue(ev.currentTarget.value);
      }}
      value={_value}
      {...props}
    />
  );
}

const toggle =
  (dispatch: React.Dispatch<React.SetStateAction<boolean>>) => () =>
    dispatch((s) => !s);

const unselectedStyles: React.CSSProperties = {
  backgroundColor: "transparent",
  border: "1px dashed white",
};
const featuredStyle: React.CSSProperties = {
  backgroundColor: "var(--gm-color-alt-bg)",
  border: "1px solid var(--gm-color-alt-bg)",
};
type FeaturedCategoryButtonProps = {
  featuredCategory?: Category;
  /**
   * Used for efficient js fade out animation
   */
  contentElement: HTMLIonContentElement | null;
  onClick: () => void;
};
function FeaturedCategoryButton(props: FeaturedCategoryButtonProps) {
  const featuredRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsub = scrollTransport.subscribe((scroll) => {
      if (featuredRef.current) zoomOutOnScrollRef(featuredRef.current, scroll);
    });

    if (props.contentElement)
      scrollTransport.emit(props.contentElement.scrollTop);

    return unsub;
  }, []);

  return (
    <div>
      <div style={{ height: "var(--featured-height)" }} />
      <div
        ref={featuredRef}
        className={css.FeaturedCategory}
        style={{ padding: "0.5rem", boxSizing: "border-box" }}
      >
        <button
          onClick={props.onClick}
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "10px",
            boxSizing: "border-box",
            maxWidth: "80ch",
            margin: "auto",
            ...(props.featuredCategory ? featuredStyle : unselectedStyles),
          }}
        >
          {props.featuredCategory?.id ? (
            <IconEdit className={css.IconBtn} />
          ) : (
            <IconPlus className={css.IconBtn} />
          )}
          <div style={{ color: "white" }}>Vælg fremhævet kategori</div>
          {props.featuredCategory?.id && (
            <div style={{ color: "white", textAlign: "center" }}>
              <h3>{props.featuredCategory.name}</h3>
            </div>
          )}
        </button>
      </div>
    </div>
  );
}

type FeaturedCategoryPickerProps = {
  open?: boolean;
  categories: Category[];
  onPick: (category: Category) => void;
  onClose: () => void;
};
function FeaturedCategoryPicker(props: FeaturedCategoryPickerProps) {
  return (
    <IonModal isOpen={props.open}>
      <IonHeader>
        <IonToolbar style={{ "--background": "var(--gm-color-alt-bg)" }}>
          <IonTitle style={{ fontWeight: "bold" }} slot="start">
            Vælg kategori
          </IonTitle>
          <p slot="end" onClick={props.onClose}>
            Luk
          </p>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonList>
          {props.categories.map((c, i) => {
            return (
              <IonItem key={`featured-${c.id}`} onClick={() => props.onPick(c)}>
                {c.name}
              </IonItem>
            );
          })}
        </IonList>
      </IonContent>
    </IonModal>
  );
}

type MoveItemProps = PropsWithChildren & EditItemProps & {};
function MoveItem({ style, ...props }: MoveItemProps) {
  return (
    <EditItem
      style={{
        borderBottom: "1px solid var(--gm-color-default-bg)",
        ...style,
      }}
      {...props}
    >
      {props.children}
    </EditItem>
  );
}

type MoveLabelProps = PropsWithChildren & {
  selected?: boolean;
};
function MoveLabel(props: MoveLabelProps) {
  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        boxSizing: "border-box",
        flexDirection: "row",
        gap: "0.5rem",
        padding: "0.5rem",
        borderRadius: "5px",
        outline: props.selected
          ? "2px var(--gm-color-primary-highlight) solid"
          : undefined,
      }}
    >
      <IonLabel
        style={{
          fontWeight: "bold",
          fontSize: "1.5rem",
          width: "100%",
        }}
      >
        {props.children}
      </IonLabel>
    </div>
  );
}

// ---- COMPONENTS END

function productByCategory(category: Category) {
  return (product: MenuProduct, ..._: unknown[]) =>
    !!product.categories.find((c) => category.id === c);
}

function emptyCategory(menu: Menu, categoryId: string): boolean {
  const count = menu.products.filter((p) => {
    return p.categories.find((c) => c === categoryId);
  }).length;
  return count === 0;
}

function findId(first: string) {
  return (other: string) => first === other;
}

/**
 * This copies the array and swaps the element in the copied array.
 */
function swapCopy<T>(arr: T[], lhs: number, rhs: number) {
  const copy = [...arr];
  copy[rhs] = arr[lhs];
  copy[lhs] = arr[rhs];
  return copy;
}

const scrollTransport = new Transport<number>();

function emitScroll(ev: IonContentCustomEvent<ScrollDetail>) {
  scrollTransport.emit(ev.detail.scrollTop);
}

export type EditMenuPageProps = {
  menu: Query<Menu>;
  uploadAsset?: (f: File) => Promise<ImageAsset>;
  save?: (menu: Menu) => Promise<Menu>;
};

export function EditMenuPage({
  menu = Query.done(mockMenu),
  uploadAsset,
  save,
}: EditMenuPageProps) {
  const [m, send, machine] = useMachine(editMenuMachine);

  useEffect(() => {
    send({ type: "editMode" });
  }, []);

  useEffect(() => {
    if (menu.type === "done") {
      send({ type: "updateContext", data: { menu: menu.data } });
    }
  }, [menu]);

  const contentRef = useRef<HTMLIonContentElement>(null);

  const updatedMenu = m.context.updatedMenu ?? m.context.menu;

  function addProductHandler(category: Category, position: number) {
    const defaultProduct = Product.createDefault();
    return () => {
      send(
        addProductMessage({
          product: {
            ...defaultProduct,
            id: `${window.crypto.randomUUID()}`,
            priceChanges: [],
            categories: [],
            originalPrice: defaultProduct.price,
            active: true,
          },
          categoryId: category.id,
          position: position,
        })
      );
    };
  }

  function addCategory(data?: AddCategoryChange["data"]) {
    return () => {
      log("Adding category with data", data);
      send({ type: "edit", data: { op: "addCategory", data: data ?? {} } });
    };
  }

  function addPriceChangeHandler(p: MenuProduct) {
    return () => {
      const id = window.crypto.randomUUID();
      send(
        editProductMessage({
          productId: p.id!,
          product: {
            priceChanges: [
              ...p.priceChanges,
              {
                id: id,
                period: {
                  from: TimeOfDay.parse("00:00"),
                  to: TimeOfDay.parse("06:00"),
                },
                price: 0,
              },
            ],
          },
        })
      );
    };
  }

  function removePriceChangeHandler(p: MenuProduct) {
    return (priceChange: PriceChange) => {
      send(
        editProductMessage({
          productId: p.id!,
          product: {
            priceChanges:
              p.priceChanges.filter((pc) => pc.id !== priceChange.id) ?? [],
          },
        })
      );
    };
  }

  const [toast, dismissToast] = useIonToast();

  const summary = m.context.summary;
  const anyEditChanges =
    any(summary?.addedProducts) ||
    any(summary?.changedProducts) ||
    any(summary?.removedProducts) ||
    any(summary?.addedCategories) ||
    any(summary?.changedCategories) ||
    any(summary?.removedCategories) ||
    summary?.setFeatured;

  const anyMoveChanges =
    any(summary?.movedProducts) || any(summary?.movedCategories);

  const selectedCategories = m.context.selectedCategories;
  const selectedProducts = m.context.selectedProducts;
  const anySelected = any(selectedCategories) || any(selectedProducts);

  const [alert, dismissAlert] = useIonAlert();

  const [featuredCategoryPicker, setFeaturedCategoryPicker] = useState(false);
  const featuredCategory = updatedMenu?.categories.find(
    (c) => c.id === updatedMenu.featuredCategoryId
  );

  const { waitFor } = useLoading();

  useEffect(() => {
    const sub = machine.subscribe(log);
    return sub.unsubscribe;
  }, []);

  const [showFindModal, setShowFindModal] = useState(false);

  // Used to adjust after adding a bunch af "move" elements
  const [adjustScroll, setAdjustScroll] = useState({
    adjust: false,
    yBefore: 0,
  });
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    let anchor = el?.querySelector("#move-anchor");
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const yBefore = adjustScroll.yBefore;
    const yAfter = rect.y ?? 0;
    const diff = yAfter - yBefore;

    if (
      (m.value === "moving" || m.value === "move") &&
      adjustScroll.adjust &&
      Math.abs(diff) > 0 &&
      Math.abs(yBefore) > 0
    ) {
      el.getScrollElement().then((scrEl) => {
        scrEl.scrollBy(0, diff / 4); // Not exactly sure why we divide by 4, but it works.
        setAdjustScroll({ yBefore: 0, adjust: false }); // Reset
      });
    }
  }, [m.value, adjustScroll]);

  const handlePickFeaturedCategory = useCallback((category: Category) => {
    send({
      type: "edit",
      data: {
        op: "setFeaturedCategory",
        data: { categoryId: category.id },
      },
    });
    toggle(setFeaturedCategoryPicker)();
  }, []);

  const handleMoveCategory =
    (change: MoveCategoryChange["data"]["to"]) => () => {
      selectedCategories
        .toSorted((scId1, scId2) => {
          // This is stupidly inefficient. Good enough for now.
          const a =
            m.context.updatedMenu?.categories.find(byId(scId1))?.priority ?? 0;
          const b =
            m.context.updatedMenu?.categories.find(byId(scId2))?.priority ?? 0;
          return a - b;
        })
        .forEach((sc) => {
          send({
            type: "edit",
            data: {
              op: "moveCategory",
              data: {
                categoryId: sc,
                to: change,
              },
            },
          });
        });
      send({ type: "next" });
    };

  const handleMoveProduct =
    (category: MoveProductChange["data"]["to"]) => () => {
      selectedProducts
        .toSorted((spId1, spId2) => {
          const a =
            m.context.updatedMenu?.products.find(byId(spId1))?.priority ?? 0;
          const b =
            m.context.updatedMenu?.products.find(byId(spId2))?.priority ?? 0;

          return a - b;
        })
        .forEach((sp) => {
          send({
            type: "edit",
            data: {
              op: "moveProduct",
              data: {
                productId: sp,
                to: category,
              },
            },
          });
        });

      send({ type: "next" });
    };

  const handleSelectCategory = (category: Category) => () => {
    any(selectedProducts)
      ? toast(
          "Du kan ikke vælge kategorier når du er igang med at flytter produkter.",
          1000
        )
      : send({ type: "selectCategory", data: category.id });
  };

  useEffect(() => {
    if (
      m.matches("move") &&
      (any(selectedProducts) || any(selectedCategories))
    ) {
      setAdjustScroll({
        adjust: true,
        yBefore:
          contentRef.current
            ?.querySelector("#move-anchor")
            ?.getBoundingClientRect().y ?? 0,
      });
      send({ type: "move" });
    }

    if (
      m.matches("moving") &&
      !any(selectedProducts) &&
      !any(selectedCategories)
    ) {
      send({ type: "moveMode" });
    }
  });

  const isMoving = m.matches("moving");

  async function saveMenu() {
    if (!m.context.updatedMenu || !save) return;
    const menu = await waitFor(save(m.context.updatedMenu), "Gemmer");
    send({ type: "reset", data: menu });
  }

  const handleBack = useCallback(
    async (orig: () => void) => {
      if (m.matches("edit")) {
        if (m.context.changes?.length > 0) {
          await alert({
            message:
              "Der er ændringer du ikke har gemt. Er du sikker på at du vil forsætte?",
            buttons: [
              {
                text: "Ja, slet ændringer",
                handler: () => {
                  orig();
                },
              },
              {
                text: "Nej, gem",
                handler: saveMenu,
              },
              { text: "Nej, tilbage" },
            ],
          });

          return;
        }

        orig();
        return;
      }

      send({ type: "deselectAll" });
      send({ type: "editMode" });
    },
    [m, send]
  );

  return (
    <IonPage>
      <FloatingBackButton onClick={handleBack} />
      <IonContent ref={contentRef} scrollEvents onIonScroll={emitScroll}>
        {/* Used to create the neat scroll effect */}
        <FeaturedCategoryButton
          onClick={toggle(setFeaturedCategoryPicker)}
          contentElement={contentRef.current}
          featuredCategory={featuredCategory}
        />
        <FeaturedCategoryPicker
          categories={updatedMenu?.categories ?? []}
          open={featuredCategoryPicker}
          onClose={toggle(setFeaturedCategoryPicker)}
          onPick={handlePickFeaturedCategory}
        />

        {m.matches("preview") ? (
          <div />
        ) : m.matches("move") || isMoving ? (
          <FullBleedLayout>
            <MenuCap />
            {updatedMenu?.categories.map((c, i) => {
              const products = updatedMenu?.products.filter(
                productByCategory(c)
              );

              const isSelected = Boolean(selectedCategories.find(byId(c)));
              return (
                <>
                  {i === 0 && empty(selectedProducts) && isMoving && (
                    <MenuItem>
                      <MoveItem>
                        <MoveHereButton
                          onClick={handleMoveCategory({
                            direction: "before",
                            categoryId: c.id,
                          })}
                        />
                      </MoveItem>
                    </MenuItem>
                  )}
                  <MenuItem>
                    <MoveItem onClick={handleSelectCategory(c)}>
                      <MoveLabel
                        selected={isSelected || (isSelected && isMoving)}
                      >
                        {c.name}
                      </MoveLabel>
                    </MoveItem>
                  </MenuItem>

                  {any(selectedProducts) &&
                    isMoving &&
                    emptyCategory(updatedMenu, c.id) && (
                      <MenuItem>
                        <MoveItem>
                          <MoveHereButton
                            onClick={handleMoveProduct({
                              direction: "before",
                              categoryId: c.id,
                            })}
                          />
                        </MoveItem>
                      </MenuItem>
                    )}

                  {!any(selectedCategories) &&
                    products.map((p, i) => {
                      const isSelected = selectedProducts.find(byId(p));
                      const isFirst = i === 0;
                      return (
                        <React.Fragment key={`selected-category-${c.id}`}>
                          {isFirst && isMoving && empty(selectedCategories) && (
                            <MenuItem>
                              <MoveItem>
                                <MoveHereButton
                                  key={`move-before-${p.id}`}
                                  onClick={handleMoveProduct({
                                    direction: "before",
                                    productId: p.id,
                                    categoryId: c.id,
                                  })}
                                />
                              </MoveItem>
                            </MenuItem>
                          )}

                          <MenuItem>
                            <MoveItem
                              onClick={() =>
                                send({ type: "selectProduct", data: p.id })
                              }
                            >
                              <MoveLabel
                                selected={Boolean(
                                  isSelected || (isSelected && isMoving)
                                )}
                              >
                                <div
                                  style={{
                                    paddingLeft: "0.5rem",
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    fontSize: "1rem",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  <span>{p.title}</span>
                                  <span>{p.originalPrice} kr.</span>
                                </div>
                              </MoveLabel>
                            </MoveItem>
                          </MenuItem>
                          {isMoving && (
                            <MenuItem>
                              <MoveItem>
                                <MoveHereButton
                                  key={`move-after-${p.id}`}
                                  onClick={handleMoveProduct({
                                    direction: "after",
                                    productId: p.id,
                                    categoryId: c.id,
                                  })}
                                />
                              </MoveItem>
                            </MenuItem>
                          )}
                        </React.Fragment>
                      );
                    })}
                  {isMoving && empty(selectedProducts) && (
                    <MenuItem>
                      <MoveItem>
                        <MoveHereButton
                          onClick={handleMoveCategory({
                            direction: "after",
                            categoryId: c.id,
                          })}
                        />
                      </MoveItem>
                    </MenuItem>
                  )}
                </>
              );
            })}
            <div id="move-anchor" style={{ height: 0, width: 0 }} />
            <MenuItem style={{ minHeight: "60vh" }} />
          </FullBleedLayout>
        ) : m.matches("edit") ? (
          <FullBleedLayout>
            <MenuCap />
            {updatedMenu && empty(updatedMenu.categories) && (
              <MenuItem>
                <AddButtons>
                  <AddButton onClick={addCategory()}>
                    + Tilføj din første kategori
                  </AddButton>
                </AddButtons>
              </MenuItem>
            )}
            {updatedMenu &&
              updatedMenu.categories.map((c, i) => {
                const products = updatedMenu.products.filter(
                  productByCategory(c)
                );

                const state = summary?.addedCategories.has(c.id)
                  ? "added"
                  : summary?.changedCategories.has(c.id)
                  ? "changed"
                  : "nothing";

                const prevCategory = updatedMenu.categories.at(i - 1);
                const isFirst = i === 0;
                const isLast = i === updatedMenu.categories.length - 1;

                return (
                  <React.Fragment key={`edit-${c.id}`}>
                    <MenuItem>
                      <AddButtons>
                        <AddButton
                          onClick={addCategory({
                            categoryId: c.id,
                            direction: "before",
                          })}
                        >
                          + Kategori
                        </AddButton>
                        {!isFirst ? (
                          <AddButton
                            onClick={addProductHandler(
                              prevCategory!,
                              Number.MAX_VALUE /* The end */
                            )}
                          >
                            + Produkt
                          </AddButton>
                        ) : (
                          []
                        )}
                      </AddButtons>
                    </MenuItem>
                    <MenuItem>
                      <ChangeFrame
                        id={c.id}
                        key={"change-frame-" + c.id}
                        change={state}
                      >
                        <CategoryEditsActionBar
                          changedCategories={[
                            ...(summary?.changedCategories.values() ?? []),
                          ]}
                          category={c}
                          onUndoAll={(c) =>
                            send({ type: "undoAllForCategory", data: c.id })
                          }
                          onUndo={(c) =>
                            send({ type: "undoForCategory", data: c.id })
                          }
                          onRemove={(c) =>
                            send({
                              type: "edit",
                              data: {
                                op: "removeCategory",
                                data: {
                                  categoryId: c.id,
                                },
                              },
                            })
                          }
                        />
                        <EditItem>
                          <label>Kategori</label>
                          <input
                            {...classNames(css.Input, css.Title)}
                            type="text"
                            value={c.name}
                            onChange={(ev) => {
                              send(
                                editCategoryMessage({
                                  id: c.id,
                                  name: ev.target.value,
                                })
                              );
                            }}
                          />
                        </EditItem>
                      </ChangeFrame>
                      {isLast &&
                        empty(
                          updatedMenu.products.filter(productByCategory(c))
                        ) && (
                          <AddButtons>
                            {/* Displays as the last one */}
                            <AddButton
                              onClick={addCategory({
                                categoryId: c.id,
                                direction: "after",
                              })}
                            >
                              + Kategori
                            </AddButton>
                            {empty(
                              updatedMenu.products.filter(productByCategory(c))
                            ) && (
                              <AddButton onClick={addProductHandler(c, 0)}>
                                + Produkt
                              </AddButton>
                            )}
                          </AddButtons>
                        )}
                      {products.map((p, pi) => {
                        const thumb =
                          p.coverAsset?.sizes?.find(
                            (u) => u.size === "1x1-w256"
                          )?.uri ?? p?.coverAsset?.uri;

                        const state = summary?.addedProducts.has(p.id)
                          ? "added"
                          : summary?.changedProducts.has(p.id)
                          ? "changed"
                          : "nothing";

                        function sendUndo(id: string) {
                          send({ type: "undoForProduct", data: id });
                        }
                        function sendUndoAll(id: string) {
                          send({ type: "undoAllForProduct", data: id });
                        }
                        function sendRemove(id: string) {
                          send(
                            removeProductMessage({
                              productId: id,
                            })
                          );
                        }

                        async function upload(file) {
                          if (!file || !uploadAsset) return;

                          const asset = await uploadAsset(file);
                          send(
                            editProductMessage({
                              productId: p.id,
                              product: { coverAsset: asset },
                            })
                          );
                        }

                        function sendEditPrice(price: number) {
                          send(
                            editProductMessage({
                              productId: p.id,
                              product: { price: price, originalPrice: price },
                            })
                          );
                        }

                        return (
                          <React.Fragment key={`edit-product-${p.id}`}>
                            <AddButtons key={`before-add-${p.id}`}>
                              <AddButton onClick={addProductHandler(c, pi)}>
                                + Produkt
                              </AddButton>
                            </AddButtons>
                            <ChangeFrame
                              id={p.id}
                              key={p.id}
                              change={state}
                              className={css.Product}
                            >
                              <ProductEditsActionBar
                                changedProductIds={[
                                  ...(summary?.changedProducts.values() ?? []),
                                ]}
                                productId={p.id}
                                onUndoAll={sendUndoAll}
                                onUndo={sendUndo}
                                onRemove={sendRemove}
                                active={p.active}
                                onToggleActive={() => {
                                  send(
                                    editProductMessage({
                                      productId: p.id,
                                      product: { active: !p.active },
                                    })
                                  );
                                }}
                              />
                              <EditItem>
                                <label>Billede</label>
                                <UploadButton onFile={upload}>
                                  <div className={css.ProductImageContainer}>
                                    {thumb && (
                                      <Thumbnail src={thumb} alt={p.title} />
                                    )}
                                    <IconEdit className={css.Center} />
                                  </div>
                                </UploadButton>
                              </EditItem>
                              <div className={css.Stack}>
                                <EditItem>
                                  <label>Titel</label>
                                  <input
                                    {...classNames(css.Input, css.Title)}
                                    type="text"
                                    value={p.title}
                                    onChange={(ev) => {
                                      send(
                                        editProductMessage({
                                          productId: p.id,
                                          product: { title: ev.target.value },
                                        })
                                      );
                                    }}
                                  />
                                </EditItem>
                                <EditItem>
                                  <label>Pris</label>
                                  <PriceInput
                                    className={css.Input}
                                    onPriceChange={sendEditPrice}
                                    value={p.originalPrice}
                                  />
                                </EditItem>
                              </div>
                              <EditItem className={css.FullBleed}>
                                <label>Beskrivelse</label>
                                <textarea
                                  {...classNames(css.Input)}
                                  style={{ height: "8rem" }}
                                  value={p.description}
                                  onChange={(ev) => {
                                    send(
                                      editProductMessage({
                                        productId: p.id,
                                        product: {
                                          description: ev.target.value,
                                        },
                                      })
                                    );
                                  }}
                                />
                              </EditItem>
                              <div
                                className={css.FullBleed}
                                style={{ textAlign: "left" }}
                              >
                                <h4 style={{ marginBottom: 0 }}>
                                  Daglige prisændringer
                                </h4>
                                <span
                                  style={{
                                    fontSize: "0.8rem",
                                    width: "inherit",
                                  }}
                                >
                                  <i>
                                    Godt til eks. før og efter midnat priser.
                                  </i>
                                </span>
                                <div style={{ height: "1rem" }} />
                                {p.priceChanges.map((pc) => {
                                  return (
                                    <PriceChangeInput
                                      key={`price-change-${pc.id}`}
                                      onChange={(change) => {
                                        const updatedPriceChanges =
                                          p.priceChanges.map((_pc) => {
                                            return _pc.id === change.id
                                              ? change
                                              : _pc;
                                          });

                                        send(
                                          editProductMessage({
                                            productId: p.id,
                                            product: {
                                              priceChanges: updatedPriceChanges,
                                            },
                                          })
                                        );
                                      }}
                                      onDelete={removePriceChangeHandler(p)}
                                      priceChange={pc}
                                    />
                                  );
                                })}
                                <div className="space-2" />
                                <AddPriceChangeButton
                                  onClick={addPriceChangeHandler(p)}
                                />
                              </div>
                            </ChangeFrame>
                            <AddButtons key={`after-add-${p.id}`}>
                              {
                                pi === products.length - 1 &&
                                i === updatedMenu.categories.length - 1 ? (
                                  <AddButton
                                    onClick={addCategory({
                                      categoryId: c.id,
                                      direction: "after",
                                    })}
                                  >
                                    + Kategori
                                  </AddButton>
                                ) : (
                                  []
                                ) /* We return [] so that React.Children doesnt count it */
                              }
                              {pi === products.length - 1 &&
                              i === updatedMenu.categories.length - 1 ? (
                                <AddButton
                                  onClick={addProductHandler(c, pi + 1)}
                                >
                                  + Produkt
                                </AddButton>
                              ) : (
                                []
                              )}
                            </AddButtons>
                          </React.Fragment>
                        );
                      })}
                    </MenuItem>
                  </React.Fragment>
                );
              })}
            <div className={css.MenuBottom} />
          </FullBleedLayout>
        ) : (
          <div />
        )}
      </IonContent>
      {m.matches("move") && selectedCategories.length === 0 && (
        <div {...classNames(css.FloatingMoveHint, css.IconColumns, css.Tinted)}>
          <IconInfoCircle /> Tryk på et produkt eller kategori
        </div>
      )}
      {any(selectedCategories) && m.matches("moving") && (
        <FloatingHint>
          Flytter {selectedCategories.length}{" "}
          {any(selectedCategories) &&
            selectedCategories.length > 0 &&
            selectedCategories.length &&
            `kategori${selectedCategories.length > 1 ? "er" : ""}`}
        </FloatingHint>
      )}
      {any(selectedProducts) && m.matches("moving") && (
        <FloatingHint>
          Flytter {selectedProducts.length} produkt
          {selectedProducts.length > 1 ? "er" : ""}
        </FloatingHint>
      )}
      <ActionBar>
        <div>
          <div className={css.ChangeList}>
            {any(summary?.addedProducts) ||
              (any(summary?.addedCategories) && (
                <span className="fg-green">
                  {summary!.addedProducts.size + summary!.addedCategories.size}{" "}
                  nye
                </span>
              ))}
            {any(summary?.changedProducts) ||
              (any(summary?.changedCategories) && (
                <span className="fg-warning">
                  {summary!.changedProducts.size +
                    summary!.changedCategories.size +
                    1}{" "}
                  ændringer
                </span>
              ))}
            {any(summary?.removedProducts) ||
              (any(summary?.removedCategories) && (
                <span className="fg-danger">
                  {summary!.removedProducts.size +
                    summary!.removedCategories.size}{" "}
                  slettede
                </span>
              ))}
          </div>
        </div>
        <div>
          {anyEditChanges || anyMoveChanges || anySelected ? (
            <>
              {!anySelected && (
                <>
                  <ActionButton
                    onClick={() => {
                      alert(
                        `Er sikker på at du vil fortryde ${m.context.changes.length} ændringer?`,
                        [
                          {
                            text: "Ja",
                            handler: () => send({ type: "undoAll" }),
                          },
                          { text: "Nej" },
                        ]
                      );
                    }}
                  >
                    <IconArrowBackUpDouble />
                    <ActionLabel>Fortryd alle</ActionLabel>
                  </ActionButton>
                  <ActionButton onClick={() => send({ type: "undo" })}>
                    <IconArrowBackUp />
                    <ActionLabel>Fortryd</ActionLabel>
                  </ActionButton>
                </>
              )}

              {m.matches("moving") && (
                <ActionButton
                  onClick={() => {
                    setAdjustScroll({
                      adjust: true,
                      yBefore:
                        contentRef.current
                          ?.querySelector("#move-anchor")
                          ?.getBoundingClientRect().y ?? 0,
                    });
                    send({ type: "moveMode" });
                  }}
                >
                  <IconArrowLeft />
                  <ActionLabel>Tilbage</ActionLabel>
                </ActionButton>
              )}

              {m.matches("move") && anySelected && (
                <>
                  <ActionButton
                    onClick={() => {
                      setAdjustScroll({
                        adjust: true,
                        yBefore:
                          contentRef.current
                            ?.querySelector("#move-anchor")
                            ?.getBoundingClientRect().y ?? 0,
                      });
                      send({ type: "move" });
                    }}
                  >
                    <IconDragDrop2 />
                    <ActionLabel>Flyt</ActionLabel>
                  </ActionButton>
                  <ActionButton onClick={() => send({ type: "deselectAll" })}>
                    <IconDeselect />
                    <ActionLabel>Afvælg</ActionLabel>
                  </ActionButton>
                </>
              )}

              <ActionButton onClick={saveMenu}>
                <IconDeviceFloppy />
                <ActionLabel>Gem</ActionLabel>
              </ActionButton>
            </>
          ) : (
            <>
              <ActionButton
                onClick={() => send({ type: "moveMode" })}
                active={m.value === "move"}
              >
                <IconHandGrab />
                <ActionLabel>Flyt</ActionLabel>
              </ActionButton>
              <ActionButton
                onClick={() => send({ type: "editMode" })}
                active={m.value === "edit"}
              >
                <IconEdit />
                <ActionLabel>Rediger</ActionLabel>
              </ActionButton>
              <ActionButton
                onClick={() => setShowFindModal(true)}
                active={showFindModal}
              >
                <IconSearch />
                <ActionLabel>Find</ActionLabel>
              </ActionButton>
              {/*<ActionButton
                onClick={() => send({ type: "previewMode" })}
                active={m.matches("preview")}
              >
                <IconEye />
                <ActionLabel>Visning</ActionLabel>
              </ActionButton>*/}
            </>
          )}
        </div>
      </ActionBar>
      <IonModal isOpen={showFindModal}>
        <IonHeader>
          <IonToolbar>
            <IonTitle style={{ fontWeight: "bold" }}>Find</IonTitle>
            <p slot="end" onClick={() => setShowFindModal(false)}>
              Luk
            </p>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {updatedMenu?.categories &&
            updatedMenu.categories.map((c, i) => {
              const gotoItem = (id: string) => {
                document
                  .getElementById(`${id}`)
                  ?.scrollIntoView({ block: "center" });
                setShowFindModal(false);
              };

              const products = updatedMenu.products.filter(
                productByCategory(c)
              );
              return (
                <>
                  <IonItem
                    key={`find-category-${c.id}`}
                    onClick={() => gotoItem(c.id)}
                  >
                    <h2>{c.name}</h2>
                  </IonItem>
                  {products.map((p, i) => {
                    return (
                      <IonItem
                        key={`find-product-${p.id}`}
                        onClick={() => gotoItem(p.id)}
                      >
                        <p>{p.title}</p>
                      </IonItem>
                    );
                  })}
                </>
              );
            })}
        </IonContent>
      </IonModal>
    </IonPage>
  );
}
